<template>
  <header
    ref="menuRef"
    class="flex w-full flex-col bg-primary-500 md:relative md:h-20 md:flex-row md:flex-nowrap md:items-center md:justify-between md:px-12 md:py-5"
  >
    <!-- Mobile layout (hidden on md+) -->
    <div class="flex flex-col md:hidden">
      <!-- Row 1: [burger][logo] ←→ flex-1 ←→ [actions] -->
      <div class="flex items-center gap-4 px-4 py-2">
        <!-- Left: burger with semi-transparent background -->
        <UiIconButton
          id="header-menu"
          class="!bg-neutral-300/50 hover:!bg-neutral-300/70 focus:!bg-neutral-300/70 disabled:!bg-disabled-300"
          :icon="IconMenu"
          :aria-haspopup="true"
          :aria-expanded="menuContext !== null"
          @click="toggle(MenuContext.MAIN)"
        />
        <!-- Logo: right next to the burger, both shrink-0 -->
        <UiLink id="header-logo-mobile-link" to="/">
          <LogoSmall class="size-8 fill-white hover:fill-primary-200" />
        </UiLink>
        <!-- Right: actions pushed to the right -->
        <div class="flex flex-1 items-center justify-end">
          <HeaderActions />
        </div>
      </div>
      <!-- Row 2: search bar -->
      <div class="px-4 pb-2">
        <UiInputSearch
          id="header-mobile-search"
          :value="route.query.search as string"
          :placeholder="$t('header.search')"
          :action="searchAction"
        />
      </div>
    </div>

    <!-- Desktop layout (hidden below md) -->
    <UiLink id="header-logo-link" to="/" class="hidden md:block">
      <Logo class="hidden w-28 fill-white hover:fill-primary-800 xl:block" />
      <LogoSmall class="size-8 fill-white hover:fill-primary-800 xl:hidden" />
    </UiLink>
    <HeaderNavigation :is-open="menuContext !== null" :toggle="toggle" />
    <HeaderMenu :menu-context="menuContext" :toggle="toggle" :menu-items="menuItems" />
    <UiInputSearch
      id="header-search"
      :value="route.query.search as string"
      :placeholder="$t('header.search')"
      :action="searchAction"
      wrapper-class="hidden !min-w-48 md:block xl:!min-w-96"
    />
    <div class="hidden md:block">
      <HeaderActions />
    </div>
  </header>
</template>

<script setup lang="ts">
import { IconMenu } from '#components';
import { onClickOutside } from '@vueuse/core';

import type { Snippet } from '~/components/ui/Input/types';
import type { Category } from '~/stores/category/types';
import type { Link, MenuSection } from '~/stores/cms/types';
import type { Product } from '~/stores/product/types';
import type { Locale } from '~/utils/common';

import menu from '~/assets/data/menu.json';
import LogoSmall from '~/assets/images/logo-small.svg';
import Logo from '~/assets/images/logo.svg';
import { type MainMenu, MenuContext, type MenuItem } from '~/components/Header/types';
import { useAppStore } from '~/stores/app/appStore';
import { useCategoryStore } from '~/stores/category/categoryStore';
import { useCmsStore } from '~/stores/cms/cmsStore';
import { useProductStore } from '~/stores/product/productStore';

const { locale, t } = useI18n();
const route = useRoute();
const {
  proxy: { dataLayer },
} = useScriptGoogleTagManager();

const categoryStore = useCategoryStore();
const productStore = useProductStore();
const appStore = useAppStore();
const cmsStore = useCmsStore();

const menuRef = ref<HTMLDivElement | null>(null);
const menuContext = ref<MenuContext | null>(null);
const menuItems = ref<MenuItem[]>([]);

const categoriesAsyncData = await useAsyncData(
  `categories-${locale.value}`,
  async () => await categoryStore.fetchCategories(locale.value as Locale),
);

const { data: categoriesData } = categoriesAsyncData;

const menuAsyncData = await useAsyncData(
  `menu-${locale.value}`,
  async () => await cmsStore.fetchMenu(locale.value as Locale),
);

const { data: menuData } = menuAsyncData;

const toggle = (context: MenuContext | null): void => {
  if (context === null) close();
  else {
    dataLayer.push({
      component: 'header',
      event: 'button_click',
      value: `menu_${context}`,
    });

    switch (context) {
      case MenuContext.CATEGORIES:
        menuItems.value = categoriesData.value?.map(mapCategoryToMenuItem) ?? [];
        if (menuItems.value.length) {
          menuContext.value = MenuContext.CATEGORIES;
        }
        break;
      case MenuContext.CMS:
        menuItems.value = menuData.value?.menu_sections.map(mapMenuToMenuItem) ?? [];
        if (menuItems.value.length) {
          menuContext.value = MenuContext.CMS;
        }
        break;

      case MenuContext.MAIN:
        menuItems.value = menu.map(mapMainMenuToMenuItem);
        menuContext.value = MenuContext.MAIN;
        break;
    }
  }
};

const close = (): void => {
  menuItems.value = [];
  menuContext.value = null;
};

const mapMainMenuToMenuItem = (mainMenu: MainMenu): MenuItem => ({
  action: mainMenu.openMenu ? (): void => toggle(mainMenu.key as MenuContext) : undefined,
  children: [],
  key: mainMenu.key,
  label: mainMenu.label ? t(mainMenu.label) : (cmsStore.menu?.title ?? ''),
  url: mainMenu.url,
});

const mapCategoryToMenuItem = (category: Category): MenuItem => ({
  children: category.categories?.map(mapCategoryToMenuItem) ?? [],
  key: category.id,
  label: category.name[locale.value as Locale],
  url: category.slug[locale.value as Locale],
});

const mapLinkToMenuItem = (link: Link): MenuItem => ({
  children: [],
  key: link.id.toString(),
  label: link.name,
  url: link.url,
});

const mapMenuToMenuItem = (menuSection: MenuSection): MenuItem => ({
  children: menuSection.links?.map(mapLinkToMenuItem) ?? [],
  key: menuSection.id.toString(),
  label: menuSection.title,
  url: menuSection.url,
});

const mapProductToSnippet = (product: Product): Snippet => ({
  href: product.slug[locale.value as Locale],
  id: product.id,
  image: product.images?.[0],
  name: product.name,
  text: formatPrice(locale.value as Locale, getPrice(product, appStore.getCurrentCurrency)?.amount ?? 0, Currency.EUR),
});

const searchAction = async (query: string): Promise<Snippet[] | null> => {
  dataLayer.push({
    component: 'header',
    event: 'search',
    value: query,
  });

  return (await productStore.searchProducts(query, locale.value as Locale, true))?.map(mapProductToSnippet) ?? null;
};

onClickOutside(menuRef, close);

watch(locale, () => {
  categoriesAsyncData.refresh();
  menuAsyncData.refresh();
});
</script>
